// packages/ui/src/ImageCropEditor/imageAlpha.ts
//
// ROADMAP #135 (2026-10-09) — a picture with a transparent background came back from the editor on BLACK: the editor
// writes PNG only when it knows the picture has transparency, and it never looked at the picture it was given (only a
// background it removed itself counted). A canvas exported as JPEG paints every transparent pixel black, and the upload
// paths then named and typed the result `.jpg` / `image/jpeg` whatever it was.
// ⚠ The format is decided by the PIXELS being written, never by the type a file declares: a browser types a picked file
//   by its EXTENSION, so a transparent PNG named `logo.jpg` says `image/jpeg` (review 2026-10-09).
// No DOM at import: the canvas work takes the 2D context it is given, so a guard can run all of it.
import type React from "react";

export const IMAGE_MIME = {
  PNG: "image/png",
  JPEG: "image/jpeg",
} as const;

/** The extension a stored image is named with, by its type. */
export const IMAGE_EXTENSION_BY_MIME: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "image/avif": ".avif",
};

/** The type an edited image is uploaded as when neither its blob nor its name says. */
export const EDITED_IMAGE_FALLBACK_MIME = IMAGE_MIME.JPEG;

/** What a picture written as a JPEG is laid on, should a transparent pixel ever reach one (never black). */
export const JPEG_FLATTEN_COLOR = "#ffffff";

/** Whether RGBA pixel data has any pixel that is not fully opaque. */
export const pixelsHaveAlpha = (data: ArrayLike<number>): boolean => {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }

  return false;
};

/**
 * Whether a picture has any transparent pixel. `readPixels` returns its RGBA data, or null when it cannot be read (a
 * cross-origin picture). Whatever type the file claims, the pixels are read: a JPEG simply reads as all opaque.
 */
export const imageHasAlpha = (readPixels: () => ArrayLike<number> | null): boolean => {
  const data = readPixels();

  return !!data && pixelsHaveAlpha(data);
};

/** The part of a 2D context the encoder uses (a guard passes a fake). */
export interface PixelContext {
  getImageData: (sx: number, sy: number, sw: number, sh: number) => { data: ArrayLike<number> };
  fillRect: (x: number, y: number, w: number, h: number) => void;
  fillStyle: unknown;
  globalCompositeOperation: string;
}

/** A context's pixels, or null when the browser will not let us read them. */
export const readContextPixels = (ctx: PixelContext, width: number, height: number): ArrayLike<number> | null => {
  try {
    return ctx.getImageData(0, 0, width, height).data;
  } catch {
    return null;
  }
};

/**
 * The type to write a drawn canvas as: PNG when any of ITS pixels is transparent, else JPEG. A JPEG is first laid on
 * white (behind what is drawn), so a pixel the read missed can never come out black.
 */
export const encodeMimeFor = (ctx: PixelContext, width: number, height: number): string => {
  if (imageHasAlpha(() => readContextPixels(ctx, width, height))) {
    return IMAGE_MIME.PNG;
  }

  ctx.globalCompositeOperation = "destination-over";
  ctx.fillStyle = JPEG_FLATTEN_COLOR;
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = "source-over";

  return IMAGE_MIME.JPEG;
};

/** The type a file name says it is ("logo.PNG" → image/png), or null. */
export const imageMimeFromName = (name: string | null | undefined): string | null => {
  const ext = (String(name || "").match(/\.([^./]+)$/)?.[1] ?? "").toLowerCase();

  if (!ext) {
    return null;
  }

  const dotted = `.${ext === "jpeg" ? "jpg" : ext}`;

  return Object.keys(IMAGE_EXTENSION_BY_MIME).find((mime) => IMAGE_EXTENSION_BY_MIME[mime] === dotted) ?? null;
};

/** The type an edited / skipped image is uploaded as: its blob's own, else what its name says, else a JPEG. */
export const editedImageMime = (blob: Blob, name?: string | null): string =>
  blob.type || imageMimeFromName(name) || EDITED_IMAGE_FALLBACK_MIME;

/**
 * `name` with the extension its type is stored with ("logo.jpg" + image/png → "logo.png"). A name that already says its
 * type ("photo.jpeg") is kept; so is any name when the type is not one we know (it is not ours to rename).
 */
export const imageFileName = (name: string, mime: string): string => {
  const given = String(name || "") || "image";
  const type = String(mime || "").toLowerCase();
  const ext = IMAGE_EXTENSION_BY_MIME[type];

  if (!ext) {
    return given;
  }

  if (imageMimeFromName(given) === type) {
    return given;
  }

  return `${given.replace(/\.[^./]+$/, "") || "image"}${ext}`;
};

/**
 * The checkerboard drawn behind a picture so its transparent parts read as transparent, not as the colour behind them.
 * A conic checker tiles seamlessly (no edge seams the offset linear-gradient version left behind).
 */
export const TRANSPARENCY_CHECKER: React.CSSProperties = {
  backgroundImage: "repeating-conic-gradient(#6f6f6f 0% 25%, #8f8f8f 0% 50%)",
  backgroundSize: "20px 20px",
};
